import React, { useState } from 'react';
import { formatDate, formatNumber } from '../../Utils/Format';

type BarChartProps = {
  data: { date: string; count: number }[];
  unit?: string;
  /** Formats values in the peak label and tooltip (defaults to plain numbers). */
  format?: (value: number) => string;
  /** Extra tooltip text for one day, shown after the value. */
  detail?: (index: number) => React.ReactNode;
};

/** Single series daily bar chart (one hue, 2px gaps, hover tooltip). */
const BarChart: React.FC<BarChartProps> = ({ data, unit = 'sign ups', format = formatNumber, detail }) => {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...data.map((d) => d.count));
  const label = (date: string) => formatDate(`${date}T12:00:00`).replace(/, \d{4}$/, '');
  const tipLeft = hover === null ? 0 : ((hover + 0.5) / data.length) * 100;
  // Keep the tooltip inside the plot near the edges.
  const tipShift = tipLeft < 18 ? '-12%' : tipLeft > 82 ? '-88%' : '-50%';
  return (
    <div className="bar-chart">
      <div className="bar-chart__max">Peak {format(max)} a day</div>
      <div
        className={`bar-chart__plot${data.length > 40 ? ' bar-chart__plot--dense' : ''}`}
        onMouseLeave={() => setHover(null)}
        role="img"
        aria-label={`Daily ${unit}`}
      >
        {data.map((d, index) => (
          <div key={d.date} className="bar-chart__bar" onMouseEnter={() => setHover(index)}>
            <span style={{ height: `${(d.count / max) * 100}%` }} />
          </div>
        ))}
        {hover !== null && data[hover] && (
          <div className="bar-chart__tip" style={{ left: `${tipLeft}%`, transform: `translate(${tipShift}, -100%)` }}>
            <strong>{format(data[hover].count)}</strong> {unit}
            {detail ? <>, {detail(hover)}</> : null} · {label(data[hover].date)}
          </div>
        )}
      </div>
      {data.length > 0 && (
        <div className="bar-chart__axis">
          <span>{label(data[0].date)}</span>
          <span>{label(data[data.length - 1].date)}</span>
        </div>
      )}
    </div>
  );
};

export default BarChart;
